/**
 * Collections Services can be used to create observable arrays services which
 * are used to manage items in a collection.
 *
 * The main purpose of it is to create services which needs to handle a number of items (eg.
 * a list of menu items, a list of widgets, etc.) and provide methods to add, remove, update,
 * and clear items in the collection.
 *
 * Collections has default items which are added to the collection at initialization and later
 * can be changed them together. This is very practical when the list need to be filled with
 * configuration-based items, but later these items needs to be reconfigured dynamically.
 *
 * ## Registering Collections
 *
 * To create (register) service methods for to manage a collection the
 * {@link "AppFrame Services Interface".Services.registerCollection | registerCollection} service
 * method must be used.
 *
 * ## Updating Default Items
 *
 * To update the default items of a collection, use the
 * {@link CollectionServices.setDefaultItems | setDefaultItems} function.
 * This function takes an array of item configurations and updates the collection items
 * with it or them.
 *
 * ## Adding Items
 *
 * To add items to a collection, use the {@link CollectionServices.addItem | addItem} function.
 * This function takes a single item or an array of items and adds them to the collection.
 * If the item has an `order` property, it is used to determine the order of the item in the
 * collection.
 *
 * ## Updating Items
 *
 * To update items in a collection, use the {@link CollectionServices.updateItem | updateItem}
 * function.
 * This function takes a single item or an array of items and updates them in the collection.
 *
 * ## Removing Items
 *
 * To remove items from a collection, use the  {@link CollectionServices.removeItem | removeItem}
 * function.
 * This function takes a single item, an array of items, a single item ID, or an array of
 * item IDs.
 *
 * It is also possible to remove all items in the collection with the
 * {@link CollectionServices.clear | clear} function.
 *
 * ## Retrieving Items
 * To retrieve all items in a collection, use the {@link CollectionServices.getItems | getItems}
 * function.
 * This function returns an array of all items in the collection sorted by their order.
 *
 * It is also possible to retrieve a single item at a specific index with the
 * {@link CollectionServices.getItemAt | getItemAt} function.
 *
 * ## Subscribe to Item Changes
 *
 * To listen for changes to items in a collection, use the
 * {@link CollectionServices.onItemChange | onItemChange} function.
 * This function takes a callback function that is called whenever an item is added, removed,
 * or updated in the collection.
 * The callback receives a reason parameter that indicates the type of change.
 *
 * ## Example Using Collections Service in React
 *
 * The following hook is a simple way to integrate the collection services into a React component:
 * ```ts
 * const useCollectionServices = <Config extends CollectionConfig, Item extends CollectionItem>(
 *   collectionServices: CollectionServices<Config, Item>,
 * ) => {
 *   const [items, setItems] = useState(collectionServices.getItems);
 *
 *   useEffect(() => {
 *     const unsubscribe = collectionServices.onItemChange(() => {
 *       setItems(collectionServices.getItems());
 *     });
 *
 *     return unsubscribe;
 *   }, [collectionServices]);
 *
 *   return items;
 * };
 * ```
 *
 * It uses the `useState` and `useEffect` hooks to manage the state of the collection items
 * and to subscribe to changes in the collection. This allows the component to re-render
 * whenever the collection items change.
 *
 * Use it like this:
 * ```ts
 * const MyComponent = () => {
 *   const items = useCollectionServices(services.myModule.myCollection);
 *   // ... rest of the component logic ...
 * };
 * ```
 *
 * @module Services > Basic Services > Collections
 */
import { UnsubscribeMethod } from '../../common/base';

/**
 * The base configuration object for a collection item.
 */
export interface CollectionConfig {
  /**
   * The unique identifier of the collection item.
   *
   * This is used to identify the item in the collection.
   * If it is not specified, it will be automatically generated.
   */
  id?: string;

  /**
   * The order of the item in the collection.
   *
   * When multiple items have the same order, they are sorted by their registered order.
   *
   * @default 1000
   */
  order?: number;
}

/**
 * The type of the collection item contains all properties of the collection config
 * but resolved with default values where `CollectionConfig` did not contain any value.
 */
export interface CollectionItem extends Required<CollectionConfig> {}

/**
 * Add a new item or items to the collection.
 *
 * @param item The item or items to add to the collection.
 * @return The added item or items.
 */
export type AddCollectionItemService<Config extends CollectionConfig, Item extends CollectionItem> = {
  (item: Config): Item;
  (item: Config[]): Item[];
};

/**
 * Update an existing item or items in the collection.
 *
 * The items must have the same `id` as the existing items in the collection.
 *
 * @param item The item or items to update in the collection.
 * @return The updated item or items.
 */
export type UpdateCollectionItemService<Item extends CollectionItem> = {
  (item: Item): Item;
  (item: Item[]): Item[];
};

/**
 * Remove an item or items from the collection.
 *
 * The items can be specified by their `id` or by the item itself.
 *
 * @param itemOrIds The item or items to remove from the collection.
 */
export type RemoveCollectionItemService<Item extends CollectionItem> = (
  itemOrIds: Item | Item[] | Item['id'] | Item['id'][],
) => void;

/**
 * Clear the collection by removing all items.
 */
export type ClearCollectionService = () => void;

/**
 * Retrieves all (both default and normal) items in the collection.
 *
 * @returns The array of all items in the collection, sorted by their `order` property.
 */
export type GetCollectionItemsService<Item extends CollectionItem> = () => Item[];

/**
 * Retrieves an item at a specific index in the collection.
 *
 * @param index The index of the item to retrieve.
 * @returns The item at the specified index or `null` if the index is out of bounds.
 */
export type GetCollectionItemAtService<Item extends CollectionItem> = (index: number) => Item | null;

/**
 * Implies the reason why an item in the collection has changed.
 */
export enum CollectionItemChangeReason {
  /**
   * The default items of the collection have been changed
   */
  Default = 'Default',
  /**
   * One or more items have been added to the collection.
   */
  Add = 'Add',
  /**
   * One or more items have been removed from the collection.
   */
  Remove = 'Remove',
  /**
   * One or more items have been updated in the collection.
   */
  Update = 'Update',
}

/**
 * Sets the default items for the collection.
 *
 * This will replace the current default items with the new ones.
 * Default items are special items that are added to the collection at initialization and later can be changed
 * by the `setDefaultItems` method, but none of the items can be changed or removed by other methods.
 *
 * @param itemConfigs The new default items to set for the collection.
 */
export type SetDefaultCollectionItemsService<Config extends CollectionConfig> = (itemConfigs: Config[]) => void;

/**
 * Callback method which is invoked when an item in the collection changes.
 *
 * The callback is used to notify subscribers about changes in the collection,
 * such as when an item is added, removed, or updated.
 *
 * The callback is called with a reason parameter that indicates the type of change, but it does
 * not provide the changed items itself. They must be retrieved separately with the `getItems`
 * method.
 *
 * @param reason The reason why the item changed.
 */
export type OnCollectionItemChangeCallback = (reason: CollectionItemChangeReason) => void;

/**
 * Service method which is used to subscribe to item changes in the collection.
 *
 * This method allows you to listen for changes in the collection,
 * such as when an item is added, removed, or updated or default items are set.
 *
 * @param callback The callback method which is invoked when an item in the collection changes.
 * @returns A method to unsubscribe from the item change subscription.
 */
export type OnCollectionItemChangeService = (callback: OnCollectionItemChangeCallback) => UnsubscribeMethod;

/**
 * Collection Services interface defines the methods for managing an array of items in a
 * subscribable way.
 *
 * Also the items in the collection are ordered by their `order` property.
 *
 * Collection services includes service methods for adding, updating, removing, and clearing items
 * in a collection, as well as retrieving items and setting default items.
 *
 * Default items are special items which are added to the collection at initialization and later can
 * be changed by the `setDefaultItems` method, but none of the items can be changed or removed by
 * other methods.
 *
 * It is a generic interface that can be used with different types of collections.
 */
export interface CollectionServices<Config extends CollectionConfig, Item extends CollectionItem> {
  /**
   * @inheritDoc AddCollectionItemService
   *
   * @example
   * ```ts
   * services.myModule.myCollection.addItem({ id: 'item3', order: 3 });
   * services.myModule.myCollection.addItem([
   *   { id: 'item4', order: 4 },
   *   { id: 'item5', order: 5 }
   * ]);
   * ```
   */
  addItem: AddCollectionItemService<Config, Item>;

  /**
   * @inheritDoc UpdateCollectionItemService
   *
   * @example
   * ```ts
   * services.myModule.myCollection.updateItem({ id: 'item1', order: 6 });
   * services.myModule.myCollection.updateItem([
   *   { id: 'item2', order: 7 },
   *   { id: 'item3', order: 8 }
   * ]);
   * ```
   */
  updateItem: UpdateCollectionItemService<Item>;

  /**
   * @inheritDoc RemoveCollectionItemService
   *
   * @example
   * ```ts
   * services.myModule.myCollection.removeItem('item3');
   * services.myModule.myCollection.removeItem(['item4', 'item5']);
   * ```
   */
  removeItem: RemoveCollectionItemService<Item>;

  /**
   * @inheritDoc ClearCollectionService
   *
   * @example
   * ```ts
   * services.myModule.myCollection.clear();
   * ```
   */
  clear: ClearCollectionService;

  /**
   * @inheritDoc GetCollectionItemsService
   *
   * @example
   * ```ts
   * const items = services.myModule.myCollection.getItems();
   * ```
   *
   */
  getItems: GetCollectionItemsService<Item>;

  /**
   * @inheritDoc GetCollectionItemAtService
   *
   * @example
   * ```ts
   * const item = services.myModule.myCollection.getItemAt(0);
   * ```
   */
  getItemAt: GetCollectionItemAtService<Item>;

  /**
   * @inheritDoc SetDefaultCollectionItemsService
   *
   * @example
   * ```ts
   * services.myModule.myCollection.setDefaultItems([
   *   { id: 'item6', order: 1 },
   *   { id: 'item7', order: 2 }
   * ]);
   * ```
   */
  setDefaultItems: SetDefaultCollectionItemsService<Config>;

  /**
   * @inheritDoc OnCollectionItemChangeService
   *
   * @example
   * ```ts
   * services.myModule.myCollection.onItemChange((reason) => {
   *  console.log('Item change reason:', reason);
   *  console.log('Items after change:', services.myModule.myCollection.getItems());
   * });
   * ```
   */
  onItemChange: OnCollectionItemChangeService;
}

/**
 * Registers collection services methods specified by a service prefix path.
 */
export type RegisterCollectionService = (
  servicePrefix: string,
  defaultItems?: CollectionConfig[],
  defaultOrder?: number,
) => void;

declare module 'types/services/interface' {
  interface Services {
    /**
     * Registers collection services methods specified by a service prefix path.
     *
     * This method will create the following methods inside the service path:
     * - {@link CollectionServices.addItem | addItem}: Adds one or more items to the collection.
     * - {@link RemoveCollectionItemService | removeItem}: Removes one or more items from the collection by item or ID.
     * - {@link UpdateCollectionItemService | updateItem}: Updates one or more items in the collection.
     * - {@link GetCollectionItemsService | getItems}: Retrieves all items in the collection.
     * - {@link GetCollectionItemAtService | getItemAt}: Retrieves an item at a specific index in the collection.
     * - {@link SetDefaultCollectionItemsService | setDefaultItems}: Sets the default items for the collection.
     *
     * @example
     * To register the collection with default items:
     * ```ts
     * services.registerCollection('myModule.myCollection', [
     *   { id: 'item1', order: 1 },
     *   { id: 'item2', order: 2 }
     * ]);
     * ```
     *
     * The created collection items can be retrieved with
     * ```ts
     * services.myModule.myCollection.getItems();
     * ```
     * or a single item can be retrieved with
     * ```ts
     * services.myModule.myCollection.getItemAt(0);
     * ```
     *
     * @param servicePrefix The service prefix path. If it is set to `myModule.myService`. It will create
     *                      create the above mentioned methods inside `services.myModule.myService`
     *                      (eg. `services.myModule.myService.addItem`).
     * @param defaultItems The items which will be added to the collection at initialization. (Optional)
     * @param defaultOrder The default order of items which does not have an `order` property set. (Optional)
     */
    registerCollection: RegisterCollectionService;
  }
}
